import moment from 'moment'
import { type ReactElement, useMemo } from 'react'
import { useQuery } from 'urql'
import { CONTACT_STATUS_CHANGES_QUERY } from 'app/components/contacts/graphql'
import { ChangeAuthorName } from 'app/components/contacts/updates/UpdateRun'
import type { SystemListStatusField } from 'app/types/navigation'
import { statusChanges } from 'lib/systemListStatus'

const HISTORY_LIMIT = 100

export function StatusHistory({
  contactId,
  field,
}: {
  contactId: string
  field: SystemListStatusField
}): ReactElement | null {
  const variables = useMemo(
    () => ({
      search: `type:"Change" AND contacts.id.keyword:"${contactId}"`,
      pagination: { limit: HISTORY_LIMIT },
      sort: { field: `createdDate`, direction: `DESC` as const },
    }),
    [contactId],
  )
  const [{ data, fetching, error }] = useQuery({
    query: CONTACT_STATUS_CHANGES_QUERY,
    variables,
    requestPolicy: `network-only`,
  })

  if (fetching) return null
  if (error) return <p className="text-body-xs text-subtle">{`Couldn't load the history.`}</p>
  const changes = statusChanges(data?.activities?.hits ?? [], field)
  if (changes.length === 0) {
    // The read is capped, so an empty filter result only means "none here" when nothing was left out.
    const capped = (data?.activities?.total ?? 0) > (data?.activities?.hits.length ?? 0)
    return (
      <p className="text-body-xs text-subtle">
        {capped ? `No status changes in the latest ${HISTORY_LIMIT} updates.` : `No changes yet`}
      </p>
    )
  }

  return (
    <ul className="flex flex-col gap-1">
      {changes.map((change) => (
        <li key={change.id} className="text-body-xs text-default">
          <span>{change.from ? `${change.from} to ${change.to ?? ``}` : `Set to ${change.to ?? ``}`}</span>
          <ChangeAuthorName source={change.source} />
          <span className="text-minimal">{` · ${moment(change.at).format(`MMM D, YYYY`)}`}</span>
        </li>
      ))}
    </ul>
  )
}
